import { api } from '../../lib/api.js';
import { authState } from '../../lib/auth.js';
import { BaseComponent } from '../../lib/base-component.js';
import {
    CONSENT_STATUS_ERROR,
    CONSENT_STATUS_LOADED,
    CONSENT_STATUS_LOADING,
    CONSENT_VIEW_ERROR,
    CONSENT_VIEW_LIST,
    consentGateView,
} from '../../lib/consents.js';
import { navigateAfterConsent } from '../../lib/pending-oauth.js';
import { css, html } from '../../vendor/lit.min.js';

// The auth-hosted review-and-acknowledge screen (issue #43). Auth presents the
// documents at the sign-in chokepoint so every app gets the flow without
// building one; apps still enforce server-side from the consents_current claim.
//
// Documents are shown one at a time, in order: a wall of stacked agreements
// invites scrolling to the bottom and clicking through, which is the opposite
// of what a record of informed acceptance should encourage.
class ConsentView extends BaseComponent {
    static properties = {
        _documents: { type: Array, state: true },
        _status: { type: String, state: true },
        _error: { type: String, state: true },
        _submitting: { type: Boolean, state: true },
    };

    static styles = css`
        :host { display: block; }
        .consent-card { max-width: 720px; margin: 0 auto; }
        .document-body {
            max-height: 45vh;
            overflow-y: auto;
            white-space: pre-wrap;
            background: #f8f9fa;
            border: 1px solid #dee2e6;
            border-radius: 0.375rem;
            padding: 1rem;
            font-size: 0.925rem;
        }
        .progress-note { font-size: 0.875rem; }
    `;

    constructor() {
        super();
        this._documents = [];
        this._status = CONSENT_STATUS_LOADING;
        this._error = '';
        this._submitting = false;
    }

    async connectedCallback() {
        await super.connectedCallback();
        this._loadOutstanding();
    }

    async _loadOutstanding() {
        try {
            const data = await api.get('/api/v1/consents/outstanding');
            this._documents = data.documents || [];
            this._status = CONSENT_STATUS_LOADED;
            if (this._documents.length === 0) this._finish();
        } catch (err) {
            this._status = CONSENT_STATUS_ERROR;
            this._error = err.message;
        }
    }

    // Acknowledge the document on screen, then move to the next. The server
    // records the version and hash from its own copy, so the request carries
    // only which document and which version the user was actually shown.
    async _acknowledge(document) {
        this._error = '';
        this._submitting = true;
        try {
            await api.post('/api/v1/consents', {
                document_key: document.key,
                document_version: document.version,
            });
            this._documents = this._documents.filter((d) => d.key !== document.key);
            if (this._documents.length === 0) {
                await this._refreshSession();
                this._finish();
            }
        } catch (err) {
            this._error = err.message;
        } finally {
            this._submitting = false;
        }
    }

    // The token in hand still says consents are outstanding — it was minted
    // before this acknowledgement. Refreshing mints one with the claim set, so
    // the app the user is heading back to isn't turned away by its own gate.
    async _refreshSession() {
        try {
            await authState._refreshTokens();
        } catch {
            // A failed refresh isn't fatal here: the acknowledgement is
            // recorded, and the next token the session mints carries the claim.
            authState.consentsOutstanding = false;
        }
    }

    // Nothing left to acknowledge, so hand the user back to whatever sent them
    // here. navigateAfterConsent answers that from the pending request alone —
    // the sign-in resolver would refuse, since it guards against redirecting to
    // the gate we are standing on (#53).
    _finish() {
        navigateAfterConsent(sessionStorage);
    }

    render() {
        const view = consentGateView(this._status, this._documents);

        return html`
            <div class="consent-card mt-5">
                <div class="card shadow">
                    <div class="card-body p-4">
                        ${
                            view === CONSENT_VIEW_ERROR
                                ? html`
                            <h4 class="mb-3">Before you continue</h4>
                            <div class="alert alert-danger d-flex align-items-start gap-2">
                                <i class="bi bi-exclamation-triangle-fill" style="margin-top: 0.1rem;"></i>
                                <span>${this._error || 'We could not load the documents you need to review.'}</span>
                            </div>
                            <button class="btn btn-outline-secondary" @click="${this._retry}">Try again</button>
                        `
                                : view === CONSENT_VIEW_LIST
                                  ? this._renderDocument(this._documents[0])
                                  : html`<div class="text-center"><div class="spinner-border text-primary" role="status"></div></div>`
                        }
                    </div>
                </div>
            </div>
        `;
    }

    _retry() {
        this._status = CONSENT_STATUS_LOADING;
        this._error = '';
        this._loadOutstanding();
    }

    _renderDocument(document) {
        const remaining = this._documents.length;

        return html`
            <h4 class="mb-1">${document.title}</h4>
            <p class="text-muted progress-note mb-3">
                ${
                    remaining > 1
                        ? html`Please review — ${remaining} documents to acknowledge.`
                        : html`Please review before continuing.`
                }
            </p>
            ${this._error ? html`<div class="alert alert-danger py-2"><small>${this._error}</small></div>` : ''}
            <div class="document-body mb-3">${document.body}</div>
            <div class="d-flex justify-content-between align-items-center">
                <small class="text-muted">Version ${document.version}</small>
                <button class="btn btn-primary"
                    ?disabled="${this._submitting}"
                    @click="${() => this._acknowledge(document)}">
                    ${this._submitting ? 'Recording...' : 'I have read and agree'}
                </button>
            </div>
        `;
    }
}

customElements.define('consent-view', ConsentView);
export { ConsentView };
